import { useState } from 'react'
import { useFinance } from '../context/FinanceContext'
import { useSettings } from '../context/SettingsContext'
import { getThemeColors } from '../lib/constants'
import { trackCalculatorUse } from '../lib/analytics'
import PageShell from '../components/layout/PageShell'
import CarAffordability from '../components/scenarios/CarAffordability'
import HomeAffordability from '../components/scenarios/HomeAffordability'
import RentVsBuy from '../components/scenarios/RentVsBuy'
import BabyCostEstimator from '../components/scenarios/BabyCostEstimator'

const TABS = [
  { key: 'car', label: 'Car Affordability', icon: CarIcon },
  { key: 'home', label: 'Home Affordability', icon: HomeIcon },
  { key: 'rentvsbuy', label: 'Rent vs Buy', icon: CompareIcon },
  { key: 'baby', label: 'Baby Cost', icon: BabyIcon },
]

export default function LifeDecisions() {
  const { state } = useFinance()
  const { trackCalculatorOpen } = useSettings()
  const colors = getThemeColors(state.theme)
  const validTabs = TABS.map(t => t.key)
  const [activeTab, setActiveTab] = useState(validTabs.includes(state.activeTab) ? state.activeTab : 'car')

  return (
    <PageShell
      title="Life Decisions"
      subtitle="Car affordability, home buying analysis, rent vs buy, and baby cost planning"
    >
      <div className="flex gap-1 p-1 rounded-2xl glass mb-6 max-w-fit">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => { setActiveTab(key); trackCalculatorUse(key); trackCalculatorOpen(key) }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
              activeTab === key
                ? 'text-heading shadow-sm'
                : 'text-muted hover:text-heading'
            }`}
            style={activeTab === key ? {
              background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
              border: `1px solid ${colors.primary}33`,
            } : {}}
          >
            <Icon active={activeTab === key} />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      <div className="animate-fade-in">
        {activeTab === 'car' && <CarAffordability />}
        {activeTab === 'home' && <HomeAffordability />}
        {activeTab === 'rentvsbuy' && <RentVsBuy />}
        {activeTab === 'baby' && <BabyCostEstimator />}
      </div>
    </PageShell>
  )
}

function CarIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0H6.375c-.621 0-1.125-.504-1.125-1.125V14.25m17.25 0V6.375c0-.621-.504-1.125-1.125-1.125H4.125C3.504 5.25 3 5.754 3 6.375v7.875" />
    </svg>
  )
}

function HomeIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  )
}

function CompareIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
    </svg>
  )
}

function BabyIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
    </svg>
  )
}
